/**
 * LLM Model Form Component
 * 
 * Form for creating and editing LLM models
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  MenuItem,
  Alert,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { llmModelApi, llmProviderApi } from '../../services/api';
import type { LlmModel, LlmModelCreate } from '../../types';

interface LlmModelFormProps {
  open: boolean;
  onClose: () => void;
  model?: LlmModel;
}

export const LlmModelForm: React.FC<LlmModelFormProps> = ({ open, onClose, model }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [configJson, setConfigJson] = useState('{}');

  const [formData, setFormData] = useState<LlmModelCreate>({
    provider: 0,
    name: '',
    model: '',
    cost: 0, // Legacy field
    input_cost: 0,
    output_cost: 0,
    cost_threshold_tokens: 0,
    input_cost_above_threshold: 0,
    output_cost_above_threshold: 0,
    audio_input_cost_per_hour: 0,
    audio_output_cost_per_1m_chars: 0,
    prompt_cache_enabled: false,
    prompt_cache_strategy: 'auto',
    prompt_cache_read_input_cost_per_1m: 0,
    prompt_cache_write_input_cost_per_1m: 0,
    max_context_window: 0,
    is_active: true,
    audio_input_supported: false,
    audio_output_supported: false,
    config: {},
  });

  // Fetch providers for dropdown
  const { data: providersData } = useQuery({
    queryKey: ['llm-providers'],
    queryFn: () => llmProviderApi.list({}),
  });

  // Initialize form data when editing
  useEffect(() => {
    if (model) {
      setFormData({
        provider: model.provider,
        name: model.name,
        model: model.model,
        cost: model.cost || 0, // Legacy
        input_cost: model.input_cost || 0,
        output_cost: model.output_cost || 0,
        cost_threshold_tokens: model.cost_threshold_tokens || 0,
        input_cost_above_threshold: model.input_cost_above_threshold || 0,
        output_cost_above_threshold: model.output_cost_above_threshold || 0,
        audio_input_cost_per_hour: model.audio_input_cost_per_hour || 0,
        audio_output_cost_per_1m_chars: model.audio_output_cost_per_1m_chars || 0,
        prompt_cache_enabled: model.prompt_cache_enabled === true,
        prompt_cache_strategy: model.prompt_cache_strategy || 'auto',
        prompt_cache_read_input_cost_per_1m: model.prompt_cache_read_input_cost_per_1m || 0,
        prompt_cache_write_input_cost_per_1m: model.prompt_cache_write_input_cost_per_1m || 0,
        max_context_window: model.max_context_window || 0,
        is_active: model.is_active,
        audio_input_supported: model.audio_input_supported === true,
        audio_output_supported: model.audio_output_supported === true,
        config: model.config || {},
      });
      setConfigJson(JSON.stringify(model.config || {}, null, 2));
    } else {
      // Reset form for new model
      setFormData({
        provider: 0,
        name: '',
        model: '',
        cost: 0,
        input_cost: 0,
        output_cost: 0,
        cost_threshold_tokens: 0,
        input_cost_above_threshold: 0,
        output_cost_above_threshold: 0,
        audio_input_cost_per_hour: 0,
        audio_output_cost_per_1m_chars: 0,
        prompt_cache_enabled: false,
        prompt_cache_strategy: 'auto',
        prompt_cache_read_input_cost_per_1m: 0,
        prompt_cache_write_input_cost_per_1m: 0,
        max_context_window: 0,
        is_active: true,
        audio_input_supported: false,
        audio_output_supported: false,
        config: {},
      });
      setConfigJson('{}');
    }
    setError(null);
  }, [model, open]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: LlmModelCreate) => llmModelApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-models'] });
      onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to create model');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: LlmModelCreate) => llmModelApi.update(model!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-models'] });
      onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to update model');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.provider) {
      setError('Please select a provider');
      return;
    }

    let parsedConfig: Record<string, any> = {};
    if (configJson.trim()) {
      try {
        const parsed = JSON.parse(configJson);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          parsedConfig = parsed as Record<string, any>;
        } else {
          setError('Config must be a JSON object (e.g. {"key": "value"})');
          return;
        }
      } catch {
        setError('Config JSON is invalid. Please fix syntax errors.');
        return;
      }
    }

    const payload: LlmModelCreate = {
      ...formData,
      config: parsedConfig,
    };

    if (model) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleChange = (field: keyof LlmModelCreate, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={isFullscreen ? false : "md"} 
      fullWidth 
      fullScreen={isMobile || isFullscreen}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ py: isMobile ? 1.5 : 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
              {model ? 'Edit LLM Model' : 'Add LLM Model'}
            </Typography>
            {!isMobile && (
              <IconButton onClick={() => setIsFullscreen(!isFullscreen)} size="small">
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                fullWidth
                label="Provider"
                value={formData.provider}
                onChange={(e) => handleChange('provider', Number(e.target.value))}
                required
                disabled={isLoading}
                InputProps={{ style: { fontSize: 16 } }}
              >
                <MenuItem value={0}>Select Provider</MenuItem>
                {providersData?.results.map((provider) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Model Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                disabled={isLoading}
                helperText="Display name for the model"
                InputProps={{ style: { fontSize: 16 } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Model ID"
                value={formData.model}
                onChange={(e) => handleChange('model', e.target.value)}
                required
                disabled={isLoading}
                helperText="API model identifier (e.g., gpt-4)"
                InputProps={{ style: { fontSize: 16 } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Input Cost per 1M tokens"
                value={formData.input_cost}
                onChange={(e) => handleChange('input_cost', parseFloat(e.target.value) || 0)}
                disabled={isLoading}
                inputProps={{ step: 0.01, min: 0 }}
                helperText="Cost in USD per 1M input tokens"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Output Cost per 1M tokens"
                value={formData.output_cost}
                onChange={(e) => handleChange('output_cost', parseFloat(e.target.value) || 0)}
                disabled={isLoading}
                inputProps={{ step: 0.01, min: 0 }}
                helperText="Cost in USD per 1M output tokens"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Audio Input Cost per hour"
                value={formData.audio_input_cost_per_hour}
                onChange={(e) => handleChange('audio_input_cost_per_hour', parseFloat(e.target.value) || 0)}
                disabled={isLoading}
                inputProps={{ step: 0.01, min: 0 }}
                helperText="Use for duration-billed STT/audio input. Leave 0 for token pricing."
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Audio Output Cost per 1M chars"
                value={formData.audio_output_cost_per_1m_chars}
                onChange={(e) => handleChange('audio_output_cost_per_1m_chars', parseFloat(e.target.value) || 0)}
                disabled={isLoading}
                inputProps={{ step: 0.01, min: 0 }}
                helperText="Use for character-billed text-to-speech models. Leave 0 for token or external pricing."
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Prompt Caching
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Model-level provider prompt-cache controls and cached-token pricing.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.prompt_cache_enabled === true}
                      onChange={(e) => handleChange('prompt_cache_enabled', e.target.checked)}
                      disabled={isLoading}
                    />
                  }
                  label="Prompt caching"
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  Sends explicit cache controls where supported. Automatic provider cache usage is still tracked.
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                label="Prompt cache strategy"
                value={formData.prompt_cache_strategy || 'auto'}
                onChange={(e) => handleChange('prompt_cache_strategy', e.target.value)}
                disabled={isLoading || formData.prompt_cache_enabled !== true}
                helperText="How the connector applies prompt caching for this provider"
              >
                <MenuItem value="auto">Automatic/provider default</MenuItem>
                <MenuItem value="anthropic_cache_control">Anthropic cache_control</MenuItem>
                <MenuItem value="openrouter_cache_control">OpenRouter cache_control</MenuItem>
                <MenuItem value="provider_cache_key">Provider cache key</MenuItem>
                <MenuItem value="gemini_cached_content">Gemini cached content</MenuItem>
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Cache read cost per 1M input tokens"
                value={formData.prompt_cache_read_input_cost_per_1m}
                onChange={(e) => handleChange('prompt_cache_read_input_cost_per_1m', parseFloat(e.target.value) || 0)}
                disabled={isLoading}
                inputProps={{ step: 0.01, min: 0 }}
                helperText="Cached/read input token cost in USD"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Cache write cost per 1M input tokens"
                value={formData.prompt_cache_write_input_cost_per_1m}
                onChange={(e) => handleChange('prompt_cache_write_input_cost_per_1m', parseFloat(e.target.value) || 0)}
                disabled={isLoading}
                inputProps={{ step: 0.01, min: 0 }}
                helperText="Cache creation/write input token cost in USD"
              />
            </Grid>

            {/* Tiered Pricing Section */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Tiered Pricing (optional)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                For models with different rates above a token threshold (e.g. Gemini)
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Threshold (tokens)"
                value={formData.cost_threshold_tokens}
                onChange={(e) => handleChange('cost_threshold_tokens', parseInt(e.target.value) || 0)}
                disabled={isLoading}
                inputProps={{ step: 1000, min: 0 }}
                helperText="0 = no tiered pricing"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Input Cost above threshold"
                value={formData.input_cost_above_threshold}
                onChange={(e) => handleChange('input_cost_above_threshold', parseFloat(e.target.value) || 0)}
                disabled={isLoading || !formData.cost_threshold_tokens}
                inputProps={{ step: 0.01, min: 0 }}
                helperText="USD per 1M input tokens (tier 2)"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Output Cost above threshold"
                value={formData.output_cost_above_threshold}
                onChange={(e) => handleChange('output_cost_above_threshold', parseFloat(e.target.value) || 0)}
                disabled={isLoading || !formData.cost_threshold_tokens}
                inputProps={{ step: 0.01, min: 0 }}
                helperText="USD per 1M output tokens (tier 2)"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Context window tokens"
                value={formData.max_context_window}
                onChange={(e) => handleChange('max_context_window', parseInt(e.target.value, 10) || 0)}
                disabled={isLoading}
                inputProps={{ min: 0 }}
                helperText="Maximum input context window. Use 0 if unknown."
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                minRows={6}
                maxRows={14}
                label="Config JSON"
                value={configJson}
                onChange={(e) => setConfigJson(e.target.value)}
                disabled={isLoading}
                helperText="Optional JSON config for provider-specific settings. Audio support is controlled by the dedicated switch above."
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.audio_input_supported === true}
                      onChange={(e) => handleChange('audio_input_supported', e.target.checked)}
                      disabled={isLoading}
                    />
                  }
                  label="Direct audio input"
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  Enable only after this exact model has been verified to accept audio attachments in chat.
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.audio_output_supported === true}
                      onChange={(e) => handleChange('audio_output_supported', e.target.checked)}
                      disabled={isLoading}
                    />
                  }
                  label="Text-to-speech audio output"
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  Enable only after this exact model has been verified to synthesize playable speech audio.
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                    disabled={isLoading}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          px: isMobile ? 2 : 3, 
          py: isMobile ? 1.5 : 2,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0,
          '& > button': { width: isMobile ? '100%' : 'auto' }
        }}>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? 'Saving...' : model ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default LlmModelForm;
